import type {
  CloudBoardState,
  CloudCaptureStatus,
  JobControlAction,
} from "@board-preserve/contracts";

import { progressTimeRemainingPresentation } from "./job-progress-presentation";

export type ManagedCaptureState =
  | "accepted"
  | "cancelled"
  | "complete"
  | "failed"
  | "idle"
  | "paused"
  | "processing"
  | "submitting";

export type ManagedCaptureTiming = Pick<
  CloudCaptureStatus,
  "controlState" | "jobStatus" | "snapshotStatus" | "startedAt"
>;

interface ManagedBackupPanelProps {
  captureMessage: string | null;
  capturePercent: number;
  captureProgress: { completed: number; total: number | null } | null;
  capturePurpose: CloudCaptureStatus["capturePurpose"];
  captureState: ManagedCaptureState;
  captureTiming: ManagedCaptureTiming | null;
  cloudState: CloudBoardState;
  controlBusy: boolean;
  observedAt: number | null;
  onControl: (action: JobControlAction["action"]) => void;
  onHistory: () => void;
  onPlan: () => void;
  onStart: () => void;
  pendingAction: JobControlAction["action"] | undefined;
  trialAvailability: string | null;
}

type BackupStageState = "complete" | "current" | "pending";

export interface BackupStagePresentation {
  attachmentCount: number | null;
  attachmentCompleted: number | null;
  archive: BackupStageState;
  board: BackupStageState;
  phaseLabel: string;
  attachments: BackupStageState;
}

export function backupStagePresentation(
  state: ManagedCaptureState,
  timing: ManagedCaptureTiming | null,
  progress: { completed: number; total: number | null } | null,
): BackupStagePresentation {
  const status = timing?.snapshotStatus ?? "pending";
  const attachmentCount =
    progress?.total === null || progress?.total === undefined
      ? null
      : Math.max(0, progress.total - 2);
  const attachmentCompleted =
    attachmentCount === null || progress === null
      ? null
      : Math.min(attachmentCount, Math.max(0, progress.completed - 1));

  if (status === "building_archive") {
    return {
      archive: "current",
      attachmentCount,
      attachmentCompleted,
      attachments: "complete",
      board: "complete",
      phaseLabel: "Building the archive",
    };
  }

  if (status === "capturing_attachments") {
    return {
      archive: "pending",
      attachmentCount,
      attachmentCompleted,
      attachments: state === "paused" ? "pending" : "current",
      board: "complete",
      phaseLabel:
        attachmentCount === null || attachmentCompleted === null
          ? "Copying attachments"
          : `Copying attachments · ${attachmentCompleted} of ${attachmentCount}`,
    };
  }

  if (status === "complete" || status === "complete_with_warnings") {
    return {
      archive: "complete",
      attachmentCount,
      attachmentCompleted,
      attachments: "complete",
      board: "complete",
      phaseLabel: "Backup complete",
    };
  }

  if (status === "capturing_structure") {
    return {
      archive: "pending",
      attachmentCount,
      attachmentCompleted,
      attachments: "pending",
      board: state === "paused" ? "pending" : "current",
      phaseLabel: "Capturing board content",
    };
  }

  return {
    archive: "pending",
    attachmentCount,
    attachmentCompleted,
    attachments: "pending",
    board: "pending",
    phaseLabel: state === "submitting" ? "Starting backup" : "Waiting to start",
  };
}

function StageRow({
  label,
  state,
}: {
  label: string;
  state: BackupStageState;
}) {
  return (
    <li className={`backup-stage backup-stage-${state}`}>
      <span aria-hidden="true">
        {state === "complete" ? "✓" : state === "current" ? "" : "·"}
      </span>
      <strong>{label}</strong>
    </li>
  );
}

function ActiveBackup({
  captureMessage,
  capturePercent,
  captureProgress,
  capturePurpose,
  captureState,
  captureTiming,
  controlBusy,
  observedAt,
  onControl,
  pendingAction,
}: Omit<
  ManagedBackupPanelProps,
  "cloudState" | "onHistory" | "onPlan" | "onStart" | "trialAvailability"
>) {
  const stage = backupStagePresentation(
    captureState,
    captureTiming,
    captureProgress,
  );
  const eta = captureProgress
    ? progressTimeRemainingPresentation({
        completed: captureProgress.completed,
        controlState:
          captureTiming?.controlState ??
          (captureState === "paused" ? "paused" : "active"),
        lastProgressAt: observedAt,
        startedAt: captureTiming?.startedAt ?? null,
        status:
          captureTiming?.jobStatus ??
          (captureState === "paused"
            ? "paused"
            : captureState === "accepted"
              ? "pending"
              : "running"),
        total: captureProgress.total,
      })
    : "Estimating time remaining…";

  return (
    <section className="backup-job-screen" aria-live="polite">
      <div className="backup-job-progress">
        <progress
          aria-label="Backup progress"
          max={100}
          value={capturePercent}
        />
        <div>
          <span>{captureState === "paused" ? "Paused" : stage.phaseLabel}</span>
          <span>
            {capturePercent}% · {eta}
          </span>
        </div>
      </div>
      <ol className="backup-stage-list">
        <StageRow label="Board content captured" state={stage.board} />
        <StageRow label="Copying attachments" state={stage.attachments} />
        <StageRow label="Building the archive" state={stage.archive} />
      </ol>
      {captureMessage ? (
        <p className="backup-job-note">{captureMessage}</p>
      ) : null}
      <div className="backup-job-actions" aria-label="Backup controls">
        <button
          className="secondary-action"
          disabled={controlBusy}
          onClick={() => {
            onControl(captureState === "paused" ? "resume" : "pause");
          }}
          type="button"
        >
          {pendingAction === "pause"
            ? "Pausing…"
            : captureState === "paused"
              ? controlBusy
                ? "Resuming…"
                : "Resume"
              : "Pause"}
        </button>
        <button
          className="secondary-action danger"
          disabled={controlBusy}
          onClick={() => {
            onControl("cancel");
          }}
          type="button"
        >
          {pendingAction === "cancel" ? "Cancelling…" : "Cancel backup"}
        </button>
      </div>
      {capturePurpose === "restore_safety" ? (
        <p className="backup-job-context">
          This is the safety backup required before recovery onto the original
          board. Restore changes remain locked until it completes.
        </p>
      ) : null}
    </section>
  );
}

export function ManagedBackupPanel(props: ManagedBackupPanelProps) {
  const active = ["accepted", "paused", "processing", "submitting"].includes(
    props.captureState,
  );
  if (active) return <ActiveBackup {...props} />;

  const paid = props.cloudState.entitlements.effectivePlan !== "free";
  const trialUsed = props.cloudState.trial.status === "consumed";

  return (
    <section className="backup-ready-screen">
      {props.captureState === "failed" || props.captureState === "cancelled" ? (
        <div
          className={`backup-outcome backup-outcome-${props.captureState}`}
          role="status"
        >
          <strong>
            {props.captureState === "failed"
              ? "Backup stopped safely"
              : "Backup cancelled"}
          </strong>
          <p>{props.captureMessage}</p>
        </div>
      ) : null}

      {trialUsed && !paid ? (
        <div className="backup-outcome backup-outcome-trial">
          <strong>Cloud backup trial used</strong>
          <p>
            This Workspace has used its one free cloud backup.{" "}
            {props.trialAvailability ??
              "Its download window is shown in backup history."}
          </p>
          <button onClick={props.onHistory} type="button">
            View backup history
          </button>
        </div>
      ) : (
        <>
          <div className="backup-ready-copy">
            <strong>
              {paid
                ? "Create a new recovery point"
                : "Use your free cloud backup"}
            </strong>
            <p>
              {paid
                ? "Board Preserve will capture this board now, copy available attachments, and build a downloadable archive."
                : "Your Workspace includes one managed backup, kept for 48 hours after it finishes."}
            </p>
          </div>
          <button
            className="backup-start-action"
            onClick={props.onStart}
            type="button"
          >
            Backup this board
          </button>
        </>
      )}

      {!paid ? (
        <button
          className="inline-plan-action"
          onClick={props.onPlan}
          type="button"
        >
          Compare ongoing backup plans
        </button>
      ) : null}
    </section>
  );
}
